import type { WorkOsPublicUser } from "@reminder-tracker/auth";

export type AdministratorAuthenticationBoundary =
  | Readonly<{ state: "disconnected"; reason: string }>
  | Readonly<{ state: "initializing" }>
  | Readonly<{ state: "anonymous" }>
  | Readonly<{
      state: "authorized";
      user: Pick<WorkOsPublicUser, "id"> & Partial<Omit<WorkOsPublicUser, "id">>;
      /** Presentation hint only; every protected operation reauthorizes server-side. */
      permissions: ReadonlyArray<string>;
    }>
  | Readonly<{ state: "denied"; reason: string }>;

export type AdministratorAuthenticationInput = Readonly<{
  isLoading: boolean;
  user: Readonly<{ id: string; email: string; emailVerified: boolean }> | null;
  organizationId: string | null;
  permissions: ReadonlyArray<string>;
  impersonator: object | null;
  expectedOrganizationId: string;
}>;

export const administrationReadPermission = "administration:read-aggregate";

export function normalizeAdministratorAuthentication(
  input: AdministratorAuthenticationInput,
): AdministratorAuthenticationBoundary {
  if (input.isLoading) return { state: "initializing" };
  if (input.impersonator !== null) {
    return { state: "denied", reason: "Impersonated sessions cannot access Site Administration." };
  }
  if (input.user === null) return { state: "anonymous" };
  if (!input.user.emailVerified || !input.user.email.includes("@")) {
    return { state: "denied", reason: "A verified administrator email is required." };
  }
  if (input.organizationId !== input.expectedOrganizationId) {
    return {
      state: "denied",
      reason: "This account is not in the approved administration organization.",
    };
  }
  if (!input.permissions.includes(administrationReadPermission)) {
    return {
      state: "denied",
      reason: "This account does not have aggregate administration access.",
    };
  }
  return {
    state: "authorized",
    user: { id: input.user.id, email: input.user.email, emailVerified: true },
    permissions: [...new Set(input.permissions)].sort(),
  };
}

export const localAdministratorBoundary: AdministratorAuthenticationBoundary = Object.freeze({
  state: "disconnected",
  reason: "WorkOS is intentionally disconnected in the local G0 foundation.",
});
